import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Nav, { whatsappOrderUrl } from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";

const SITE = "https://riznwash.com";
const EASE = [0.22, 1, 0.36, 1];

export default function SameDayLaundryGurugram() {
  const path = "/same-day-laundry-gurugram";
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Same-day laundry pickup and delivery",
      name: "Same-Day Laundry in Gurugram",
      description: "Book by 10 AM, receive by 8 PM — same-day wash & fold across Gurugram.",
      provider: { "@type": "LocalBusiness", name: "RIZ N WASH", areaServed: "Gurugram" },
      areaServed: { "@type": "City", name: "Gurugram" },
      offers: {
        "@type": "Offer", priceCurrency: "INR", price: "119",
        priceSpecification: { "@type": "UnitPriceSpecification", priceCurrency: "INR", price: 19, unitText: "per piece" },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "RIZ N WASH", item: SITE },
        { "@type": "ListItem", position: 2, name: "Same-day laundry Gurugram", item: `${SITE}${path}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        { "@type": "Question", name: "Can I really get my laundry back the same day in Gurugram?",
          acceptedAnswer: { "@type": "Answer", text: "Yes. Book a pickup by 10 AM and we return your wash & fold order the same evening (by 8 PM) across Gurugram." } },
        { "@type": "Question", name: "Is there an extra charge for same-day laundry?",
          acceptedAnswer: { "@type": "Answer", text: "No surcharge for wash & fold. Dry cleaning and premium care follow their standard 48-hour turnaround; rush service can be arranged for a small extra fee." } },
        { "@type": "Question", name: "Which Gurugram sectors are eligible?",
          acceptedAnswer: { "@type": "Answer", text: "All over Gurugram — from Sector 14 to Sector 89, including DLF, Sushant Lok, Golf Course Road, Sohna Road and Cyber Hub." } },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="same-day-page">
      <Seo
        title="Same-Day Laundry Pickup in Gurugram · Wash by 8 PM"
        description="Same-day laundry in Gurugram — book by 10 AM, receive by 8 PM. Free doorstep pickup, hand-ironed, folded. From ₹19/piece. Available all over Gurugram."
        path={path}
        jsonLd={jsonLd}
      />
      <Nav />
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-12 md:pt-24 pb-16">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}
          className="overline mb-4">Same-day laundry · Gurugram</motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="font-display font-light text-4xl sm:text-5xl lg:text-[5.5rem] tracking-tight leading-[1.02]"
          data-testid="same-day-h1"
        >
          Book by 10.<br /><em className="italic text-rw-blue not-italic">Fresh by 8.</em>
        </motion.h1>
        <p className="mt-6 md:mt-8 text-lg md:text-xl text-rw-ink2 max-w-2xl leading-relaxed">
          Forgot laundry until Sunday night? Guests arriving tomorrow? Our same-day wash & fold picks up before 10 AM and returns folded by 8 PM the same evening — across every Gurugram sector.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href={whatsappOrderUrl("Hi RIZ N WASH! I'd like a SAME-DAY pickup today.")} target="_blank" rel="noopener noreferrer" className="btn-dark rounded-full px-6 py-3 text-sm md:text-base font-semibold" data-testid="same-day-book-btn">
            Grab today's slot on WhatsApp →
          </a>
          <a href="tel:+917303241330" className="rounded-full px-5 py-3 text-sm font-semibold border border-rw-ink/20 hover:bg-rw-ink hover:text-white transition-colors">
            Call to check slot availability
          </a>
        </div>
      </section>

      <section className="bg-rw-bg2 border-y border-rw-ink/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20">
          <div className="overline mb-3">How the same-day flow works</div>
          <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">Four steps. Twelve hours.</h2>
          <div className="grid md:grid-cols-4 gap-0 mt-12 border-t border-rw-ink/10">
            {[
              ["09:00", "Book online", "Pick the 08:00–10:00 pickup slot on /book."],
              ["09:45", "We pick up", "Runner arrives, weighs, WhatsApps a confirmation."],
              ["14:00", "Wash & iron", "Colour-sorted, machine-washed, hand-ironed."],
              ["19:00", "Delivered", "Folded, packed, back at your door by 8 PM."],
            ].map(([t, h, d], i) => (
              <div key={i} className={`p-8 border-b ${i < 3 ? "md:border-r" : ""} border-rw-ink/10`}>
                <div className="font-display text-3xl text-rw-blue">{t}</div>
                <h3 className="font-display text-xl mt-3">{h}</h3>
                <p className="text-rw-ink2 text-sm mt-2">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-10 py-20 prose prose-lg text-rw-ink2 max-w-none">
        <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-rw-ink leading-[1.1]">Same-day service · fine print</h2>
        <p>Same-day turnaround applies to standard <strong>wash &amp; fold</strong> orders picked up between 08:00 and 10:00 AM on the day. It covers cotton, cotton-blend, synthetic and everyday activewear.</p>
        <p>Dry cleaning, curtain cleaning, sneaker cleaning and Premium Care items follow their published 48-hour turnaround because they need proper drying time — rushing those risks damage, which we won't do.</p>
        <p>Free pickup and delivery apply above ₹299. Bookings after 10 AM automatically slide to next-day at no extra cost.</p>
      </section>

      <Footer />
    </div>
  );
}
